import { useMemo, useState } from "react";

// A matched txn the user partitions between the two sides of the split.
export type SplitTxn = {
  readonly id: string;
  readonly postingDate: string;
  readonly description: string;
  readonly amount: number;
  readonly expenseNature?: { readonly id: string; readonly name: string } | null;
};

const money = (value: number) =>
  value.toLocaleString("en-US", {
    style: "currency",
    currency: "USD",
    minimumFractionDigits: 2,
  });

// Escape regex metacharacters so a literal description matches itself. Mirrors
// the escaping in create-rule-dialog.
const escapeRegex = (s: string) => s.replace(/[.*+?^${}()|[\]\\]/g, "\\$&");

// Synthesize a covering regex for a set of descriptions: a deduped alternation
// of the literal (escaped) descriptions. Empty set → empty string (no rule).
export function patternFor(descriptions: ReadonlyArray<string>): string {
  const unique = [...new Set(descriptions.map((d) => d.trim()).filter(Boolean))];
  if (unique.length === 0) return "";
  if (unique.length === 1) return escapeRegex(unique[0]);
  return unique.map((d) => `(${escapeRegex(d)})`).join("|");
}

// Dedicated side-by-side split surface. The user assigns each matched txn to
// the left (kept on the original rule) or right (carved into a new rule). Each
// side shows its synthesized pattern + count; the parent commits the split.
export default function SplitDialog({
  txns,
  saving,
  error,
  onConfirm,
  onCancel,
}: {
  txns: ReadonlyArray<SplitTxn>;
  saving: boolean;
  error?: string | null;
  // The kept-side (left) txn ids. The backend synthesizes both predicates from the
  // partition (SuggestFromSelection) — an indexed PREFIX/EXACT where possible — so the
  // patterns shown here are a live preview, not what's sent.
  onConfirm: (keptTxnIds: string[]) => void;
  onCancel: () => void;
}) {
  // Default: everything stays on the left (kept) side.
  const [rightIds, setRightIds] = useState<ReadonlySet<string>>(new Set());

  const send = (id: string, toRight: boolean) => {
    setRightIds((prev) => {
      const next = new Set(prev);
      if (toRight) next.add(id);
      else next.delete(id);
      return next;
    });
  };

  const left = txns.filter((t) => !rightIds.has(t.id));
  const right = txns.filter((t) => rightIds.has(t.id));

  const regexA = useMemo(() => patternFor(left.map((t) => t.description)), [
    left,
  ]);
  const regexB = useMemo(() => patternFor(right.map((t) => t.description)), [
    right,
  ]);

  const canSplit = !saving && left.length > 0 && right.length > 0;

  return (
    <div
      className="fixed inset-0 z-50 bg-black/30 flex items-center justify-center"
      onClick={onCancel}
    >
      <div
        className="bg-white rounded-lg shadow-xl w-[820px] max-h-[85vh] overflow-y-auto p-5 space-y-3"
        onClick={(e) => e.stopPropagation()}
      >
        <h2 className="text-sm font-semibold text-gray-800">
          Split this rule into two
        </h2>
        <p className="text-xs text-gray-500">
          Move transactions to the right to carve them into a new rule with the
          same target. The original rule keeps the left side.
        </p>

        {error && <p className="text-sm text-red-600">{error}</p>}

        <div className="grid grid-cols-2 gap-3">
          <SplitColumn
            title="Kept (original rule)"
            tone="text-emerald-700"
            rows={left}
            pattern={regexA}
            actionLabel="→"
            actionTitle="Move to the new rule"
            onAction={(id) => send(id, true)}
          />
          <SplitColumn
            title="Carved (new rule)"
            tone="text-indigo-700"
            rows={right}
            pattern={regexB}
            actionLabel="←"
            actionTitle="Keep on the original rule"
            onAction={(id) => send(id, false)}
          />
        </div>

        <div className="flex items-center gap-2 pt-1">
          <button
            type="button"
            disabled={!canSplit}
            onClick={() => onConfirm(left.map((t) => t.id))}
            className="px-3 py-1.5 text-sm rounded bg-emerald-600 text-white hover:bg-emerald-700 disabled:opacity-40"
          >
            {saving ? "Splitting…" : "Split rule"}
          </button>
          <button
            type="button"
            onClick={onCancel}
            className="px-3 py-1.5 text-sm text-gray-500 hover:text-gray-800"
          >
            Cancel
          </button>
          {!canSplit && !saving && (
            <span className="text-xs text-gray-400">
              both sides need at least one transaction
            </span>
          )}
        </div>
      </div>
    </div>
  );
}

function SplitColumn({
  title,
  tone,
  rows,
  pattern,
  actionLabel,
  actionTitle,
  onAction,
}: {
  title: string;
  tone: string;
  rows: ReadonlyArray<SplitTxn>;
  pattern: string;
  actionLabel: string;
  actionTitle: string;
  onAction: (id: string) => void;
}) {
  return (
    <div className="rounded border p-2 space-y-2">
      <p className={`text-xs font-semibold uppercase ${tone}`}>
        {title} ({rows.length.toLocaleString()})
      </p>
      <code className="block font-mono text-[11px] bg-gray-50 rounded px-1.5 py-1 break-all min-h-[1.5rem]">
        {pattern || "—"}
      </code>
      <div className="max-h-64 overflow-y-auto rounded border divide-y">
        {rows.map((r) => (
          <div
            key={r.id}
            className="flex items-center gap-2 px-2 py-1 text-xs"
          >
            <span className="text-gray-400 whitespace-nowrap">
              {r.postingDate}
            </span>
            <span className="truncate flex-1" title={r.description}>
              {r.description}
              {r.expenseNature && (
                <span className="text-indigo-700 ml-1">
                  {r.expenseNature.name}
                </span>
              )}
            </span>
            <span className="text-gray-500 whitespace-nowrap">
              {money(r.amount)}
            </span>
            <button
              type="button"
              title={actionTitle}
              onClick={() => onAction(r.id)}
              className="shrink-0 text-gray-400 hover:text-gray-800"
            >
              {actionLabel}
            </button>
          </div>
        ))}
        {rows.length === 0 && (
          <p className="px-2 py-2 text-xs text-gray-400">No transactions.</p>
        )}
      </div>
    </div>
  );
}
